import { useForm, SubmitHandler } from "react-hook-form";
import Button from "../ui/button/Button";
import Label from "../form/Label";
import PasswordInput from "../form/form-elements/PasswordInput";
import useUpdateEmail from "../../hooks/Auth/useUpdateEmail";
import { useToast } from "../../context/ToastContext";
import Input from "../form/input/InputField";

import type { UpdateEmail } from "../../types/updateEmailTypes";
import { AppState } from "../../store";
import { useStoreSelector } from "../../hooks/useStoreFromContext";

const UserChangeEmailCard = () => {
  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors }
  } = useForm<UpdateEmail>();
  const updateStoreUser = useStoreSelector((s: AppState) => s.updateUser);

  const toast = useToast();

  const { updateEmail } = useUpdateEmail({
    onCompleted: (updatedEmail) => {
      if (updatedEmail.success && updatedEmail.email) {
        reset({
          email: "",
          currentPassword: ""
        });
        updateStoreUser({email: updatedEmail.email});
        toast.success(updatedEmail.message);
      } else {
        toast.error(updatedEmail.message);
      }
    },
    onError: (err) => {
      setError('currentPassword', {
        type: 'manual',
        message: 'Wrong password, please try again',
      }, { shouldFocus: true })
      if (err) return toast.error(String(err)); 
    }
  });

  const onSubmit: SubmitHandler<UpdateEmail> = async (formValues) => {
    await updateEmail(formValues);
  };

  return (
    <div className="p-5 border border-gray-200 rounded-2xl dark:border-gray-800 lg:p-6">
      <h4 className="text-lg font-semibold text-gray-800 dark:text-white/90 lg:mb-6">
        Change Email
      </h4>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col">
        <div className="grid grid-cols-1 gap-x-6 gap-y-5">
          <div>
            <Label htmlFor="currentPassword">Current Password</Label>
            <PasswordInput
              placeholder="Enter current password"
              register={register("currentPassword", {
                required: "Current password is required"
              })}
            />
            {errors.currentPassword && (
              <p className="text-sm text-red-600">{errors.currentPassword.message}</p>
            )}
          </div>
          <div className="col-span-1 lg:col-span-1">
          <Label htmlFor="email">Email Address</Label>
          <Input 
            type="email"
            {...register("email", {
              required: "Email address is required",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Please enter a valid email address"
              }
            })}
          />
        </div>
        </div>

        <div className="flex items-center gap-3 px-2 mt-6 lg:justify-end">
          <Button size="sm">
            Update Email
          </Button>
        </div>
      </form>
    </div>
  );
};

export default UserChangeEmailCard;
